import type React from 'react';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CircleDollarSignIcon, InfoIcon } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Card } from '@/components/ui/card';
import { Tooltip as RcTooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { Estate } from '@/features/estates-property/api/estate';
import { cn } from '@/lib/utils';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import type { Locale } from '@/utils/getPriceWithCurrency';
import { getPriceWithCurrency } from '@/utils/getPriceWithCurrency';

import type { EstateAnalysis } from '../api/analytics';
import {
  useValuations,
  type ValuationChange,
  type ValuationChartData,
  type ValuationValueChange,
} from '../api/valuations';
import ReceiptTextIcon from './icons/receipt-text-icon';
import type { EstateAnalysisSetting } from './modals/estate-analysis-setting-modal';
import { PrintableChart } from './printable-chart';
import { TrendArrow } from './trend-arrow';
import type { ExportPdfRef } from './types/export-pdf';

function formatYAxisTick(value: number): string {
  if (value >= 1_000_000) return `${value / 1_000_000}m`;
  if (value >= 1_000) return `${value / 1_000}k`;
  return `${value}`;
}

type GeneralPriceValuationProps = {
  data: Estate;
  estateAnalysis: EstateAnalysis;
  exportSettings: EstateAnalysisSetting;
  hideOnPrint?: boolean;
};

export const GeneralPriceValuation = forwardRef<ExportPdfRef, GeneralPriceValuationProps>(
  ({ data, estateAnalysis, exportSettings, hideOnPrint = false }, ref) => {
    const { t } = useTranslation();

    const chartRefs = {
      salesPrice: useRef<ExportPdfRef>(null),
      rentPrice: useRef<ExportPdfRef>(null),
    };

    const {
      currentQuarterRentValuation,
      currentQuarterSaleValuation,
      prevQuarterSaleValuationComparison,
      prevQuarterRentValuationComparison,
      prev3YQuarterSaleValuationComparison,
      prev3YQuarterRentValuationComparison,
      nextQuarterForecast,
      highestRentalPriceRange,
      highestSalesPriceRange,
      lowestRentalPriceRange,
      lowestSalesPriceRange,
      rentalPriceRangeChartData,
      salesPriceRangeChartData,
      currentQuarterLabel,
    } = useValuations(estateAnalysis);

    const nextQuarterForecastText = useMemo(() => {
      if (nextQuarterForecast) {
        const text: string[] = [];
        if (nextQuarterForecast.saleValuation) {
          const { changePercent, changeValue } = nextQuarterForecast.saleValuation;
          text.push(
            `${t('sales')} ${changeValue > 0 ? '+' : ''}${changePercent}%(${changeValue > 0 ? '€' : '-€'}${Math.abs(changeValue)})`
          );
        }
        if (nextQuarterForecast.rentValuation) {
          const { changePercent, changeValue } = nextQuarterForecast.rentValuation;
          text.push(
            `${t('rent')} ${changeValue > 0 ? '+' : ''}${changePercent}%(${changeValue > 0 ? '€' : '-€'}${Math.abs(changeValue)})`
          );
        }
        if (text.length) {
          return text.join(', ');
        }
      }
    }, [t, nextQuarterForecast]);

    const confidentExplaining = useMemo(() => {
      if (nextQuarterForecast?.confidence) {
        // good, medium and poor are constants defined by PriceHubble
        switch (nextQuarterForecast.confidence) {
          case 'good':
            return t('highConfidentExplaining');
          case 'medium':
            return t('mediumConfidentExplaining');
          case 'poor':
            return t('lowConfidentExplaining');
          default:
            return;
        }
      }
    }, [t, nextQuarterForecast]);

    useImperativeHandle(ref, () => ({
      startExportPdf: async () => {
        const exportPdfProcesses = Object.values(chartRefs).map((el) =>
          el.current?.startExportPdf?.()
        );
        await Promise.all(exportPdfProcesses);
      },
      finishExportPdf: async () => {
        Object.values(chartRefs).map((el) => el.current?.finishExportPdf?.());
      },
    }));

    return (
      <div className={cn('print:mt-4', hideOnPrint && 'print:hidden')}>
        <div className="grid grid-cols-3 gap-6 print:gap-2">
          {/* Sales Price */}
          <ContainerCard
            printHidden={!exportSettings.salesPrice}
            title={t('salesPrice')}
            icon={CircleDollarSignIcon}
            gradient
          >
            <div className="flex flex-col gap-2 print:gap-0">
              <p className="text-[32px] font-extralight italic print:text-xs print:font-bold print:leading-tight">
                {currentQuarterSaleValuation
                  ? getPriceWithCurrency(currentQuarterSaleValuation.salePrice, 'EUR')
                  : '----'}
              </p>
              <ValuationComparison data={prevQuarterSaleValuationComparison} />
              <ValuationComparison data={prev3YQuarterSaleValuationComparison} />
            </div>
          </ContainerCard>

          {/* Rental price */}
          <ContainerCard
            printHidden={!exportSettings.rentalPrice}
            title={t('rentalPrice')}
            icon={CircleDollarSignIcon}
            gradient
          >
            <div className="flex flex-col gap-2 print:gap-0">
              <p className="text-[32px] font-extralight italic print:text-xs print:font-bold print:leading-tight">
                {currentQuarterRentValuation
                  ? getPriceWithCurrency(currentQuarterRentValuation.rentGross, 'EUR')
                  : '----'}
              </p>
              <ValuationComparison data={prevQuarterRentValuationComparison} />
              <ValuationComparison data={prev3YQuarterRentValuationComparison} />
            </div>
          </ContainerCard>

          {/* Confidence */}
          <ContainerCard
            printHidden={!exportSettings.confidence}
            title={t('confidence')}
            icon={CircleDollarSignIcon}
            gradient
          >
            <div className="flex flex-col gap-2 print:gap-0">
              <div className="flex items-center gap-3">
                <p className="text-[32px] font-medium capitalize italic print:text-xs print:leading-tight">
                  {getRenderValue(nextQuarterForecast?.confidence)}
                </p>
                {confidentExplaining ? (
                  <RcTooltip>
                    <TooltipTrigger>
                      <InfoIcon className="relative top-0.5 h-4 w-4 print:hidden" />
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-96 shadow">
                      {confidentExplaining}
                    </TooltipContent>
                  </RcTooltip>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <p className="font-extralight italic print:text-[9px] print:leading-tight">
                  {getRenderValue(nextQuarterForecast?.label)} {t('forecast')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <p className="font-extralight italic print:text-[9px] print:leading-tight">
                  {nextQuarterForecastText}
                </p>
              </div>
            </div>
          </ContainerCard>

          {/* Sales Price Range */}
          <ContainerCard
            printHidden={!exportSettings.salesPriceRange}
            title={t('salesPriceRange')}
            icon={ReceiptTextIcon}
            className="row-span-2"
          >
            <div className="grid grid-cols-1 gap-1 2xl:grid-cols-2 print:grid-cols-2">
              <HighLowCard title={t('high')} value={highestSalesPriceRange} />
              <HighLowCard title={t('low')} value={lowestSalesPriceRange} />
            </div>
            <ValuationChart
              ref={chartRefs.salesPrice}
              data={salesPriceRangeChartData}
              currentQuarterLabel={currentQuarterLabel}
            />
          </ContainerCard>

          {/* Rental Price Range */}
          <ContainerCard
            printHidden={!exportSettings.rentalPriceRange}
            title={t('rentalPriceRange')}
            icon={ReceiptTextIcon}
            className="row-span-2"
          >
            <div className="grid grid-cols-1 gap-1 2xl:grid-cols-2 print:grid-cols-2">
              <HighLowCard title={t('high')} value={highestRentalPriceRange} />
              <HighLowCard title={t('low')} value={lowestRentalPriceRange} />
            </div>
            <ValuationChart
              ref={chartRefs.rentPrice}
              data={rentalPriceRangeChartData}
              currentQuarterLabel={currentQuarterLabel}
            />
          </ContainerCard>

          {/* Coordinates */}
          <ContainerCard
            printHidden={!exportSettings.coordinates}
            icon={ReceiptTextIcon}
            title={t('coordinates')}
          >
            <div className="grid grid-cols-[100px_1fr] items-baseline gap-1">
              <p className="col-span-2 mb-1 print:text-[10px]">
                {getRenderValue(data?.address?.address)}
              </p>
              <p className="text-base print:text-[10px]">{t('latitude')}:</p>
              <p className="text-[28px] font-semibold leading-tight print:text-sm">
                {getRenderValue(data?.address?.lat)}
              </p>
              <p className="text-base print:text-[10px]">{t('longitude')}:</p>
              <p className="text-[28px] font-semibold leading-tight print:text-sm">
                {getRenderValue(data?.address?.long)}
              </p>
            </div>
          </ContainerCard>

          {/* Currency / Scores */}
          <ContainerCard
            printHidden={!exportSettings.currency}
            icon={ReceiptTextIcon}
            title={t('currency')}
          >
            <div className="flex flex-col gap-0.5">
              <p className="text-[32px] font-semibold print:text-sm">
                {data.estateDetails?.currency || 'EUR'}
              </p>
            </div>
          </ContainerCard>
        </div>
      </div>
    );
  }
);

const ContainerCard: React.FC<
  React.PropsWithChildren & {
    gradient?: boolean;
    printHidden?: boolean;
    className?: string;
    title?: string;
    rightTitle?: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
  }
> = ({ children, className, title, gradient, rightTitle, icon: Icon, printHidden = false }) => {
  return (
    <>
      <Card
        className={cn(
          'flex flex-col justify-between gap-4 p-6 print:gap-0 print:rounded-sm print:p-2',
          gradient && 'bg-gradient-to-r from-primary to-chart-6 text-white',
          className,
          printHidden && 'print:hidden'
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full print:h-4 print:w-4',
                gradient ? 'bg-white/10' : 'bg-muted'
              )}
            >
              <Icon className="h-4 w-4 text-white print:h-2 print:w-2" />
            </div>
            <h3 className="whitespace-nowrap text-[18px] font-normal capitalize print:text-[10px]">
              {title}
            </h3>
          </div>
          {rightTitle ? (
            <span className="text-sm text-primary-light print:text-[8px]">{rightTitle}</span>
          ) : null}
        </div>
        {children}
      </Card>
      <div className={cn('hidden', printHidden && 'print:block')} />
    </>
  );
};

const HighLowCard: React.FC<
  React.PropsWithChildren<{
    title: string;
    value?: ValuationValueChange;
  }>
> = ({ title, value }) => {
  const { i18n } = useTranslation();

  return (
    <Card className="flex-1 rounded-md bg-card-foreground p-4 text-white print:rounded-sm print:p-2">
      <div className="flex items-center justify-between gap-1">
        <p className="text-xs font-extralight print:text-[8px]">{title}</p>
        <p className="text-xs font-normal print:text-[8px]">{value?.label || '----'}</p>
      </div>
      <div className="mt-2 flex items-center gap-1 print:mt-0 print:gap-0">
        <p className="text-base print:text-[8px] print:leading-normal">
          {value
            ? getPriceWithCurrency(Math.abs(value.value), 'EUR', i18n.language as Locale, {
                maximumFractionDigits: 0,
              })
            : '----'}
        </p>
        <div className="py-0.25 flex items-center gap-2 rounded-lg bg-primary-light/5 px-2 print:gap-0.5 print:px-0.5">
          <TrendArrow changeValue={Number(value?.changePercent)} />
          <p className="text-sm print:text-[8px] print:leading-normal">
            {value?.changePercent !== undefined ? `${value.changePercent}%` : '----'}
          </p>
        </div>
      </div>
    </Card>
  );
};

type ValuationChartProps = {
  data?: ValuationChartData[];
  currentQuarterLabel?: string;
};

const ValuationChart = forwardRef<ExportPdfRef, ValuationChartProps>(
  ({ currentQuarterLabel, data }, ref) => {
    const { t, i18n } = useTranslation();
    return (
      <PrintableChart ref={ref} className="h-44 print:h-28">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={1} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="quarter"
              strokeWidth={0}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y}
                  dy={16}
                  textAnchor="middle"
                  fill="currentcolor"
                  className={cn(
                    'mt-2 text-xs',
                    payload.value === currentQuarterLabel ? 'text-primary' : 'text-text-secondary'
                  )}
                >
                  {payload.value}
                </text>
              )}
            />
            <YAxis
              interval={0}
              strokeWidth={0}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y}
                  dy={4}
                  dx={-50}
                  className="text-md font-normal text-primary-darker"
                  textAnchor="start"
                  fill="currentcolor"
                >
                  {payload.value ? formatYAxisTick(payload.value) : '0'}
                </text>
              )}
            />
            <CartesianGrid vertical={false} strokeWidth={1} strokeDasharray={4} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const item = payload[0];
                  if (!item?.payload) {
                    return null;
                  }
                  const { value, prevQuarterLabel, prevQuarterChangePercent } = item.payload;
                  return (
                    <Card className="max-w-48 rounded-md bg-card-foreground p-4 text-[16px] text-white">
                      <div className="flex items-center gap-1">
                        <p className="font-bold">
                          {typeof value === 'number'
                            ? getPriceWithCurrency(value, 'EUR', i18n.language as Locale, {
                                maximumFractionDigits: 0,
                              })
                            : '----'}
                        </p>
                        {typeof prevQuarterChangePercent === 'number' ? (
                          <div className="flex items-center gap-2 rounded-lg bg-primary-light/5 px-2 py-0.5">
                            <TrendArrow changeValue={prevQuarterChangePercent} />
                            <p>{prevQuarterChangePercent.toFixed(2)}%</p>
                          </div>
                        ) : null}
                      </div>
                      {prevQuarterLabel ? (
                        <p>
                          {t('vs')} {prevQuarterLabel}
                        </p>
                      ) : null}
                    </Card>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="value" fill="url(#colorValue)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </PrintableChart>
    );
  }
);

const ValuationComparison: React.FC<{
  data?: ValuationChange;
}> = ({ data }) => {
  const { t, i18n } = useTranslation();

  if (!data) {
    return <div>----</div>;
  }

  return (
    <div className="flex items-center gap-2 print:text-[9px] print:leading-tight">
      <TrendArrow changeValue={data.changeValue} color={false} />
      <p className="font-extralight italic">
        {data.changeValue > 0 ? '+' : ''}
        {data.changePercent}% ({data.changeValue > 0 ? '' : '-'}
        {getPriceWithCurrency(Math.abs(data.changeValue), 'EUR', i18n.language as Locale, {
          maximumFractionDigits: 0,
        })}
        )
      </p>
      <p className="font-normal">
        {t('vs')} {data.label}
      </p>
    </div>
  );
};
